/**
 * Offline Storage Service
 * Uses IndexedDB for persistent offline data storage
 */

const DB_NAME = 'swoopy_offline';
const DB_VERSION = 1;

// Store names
const STORES = {
  TRIPS: 'trips',
  DESTINATIONS: 'destinations',
  PACKING_LISTS: 'packingLists',
  OFFLINE_DATA: 'offlineData'
};

class OfflineStorageService {
  constructor() {
    this.db = null;
    this.initPromise = null;
  }

  async init() {
    if (this.db) return this.db;
    if (this.initPromise) return this.initPromise;

    this.initPromise = new Promise((resolve, reject) => {
      const request = indexedDB.open(DB_NAME, DB_VERSION);

      request.onerror = () => reject(request.error);
      request.onsuccess = () => {
        this.db = request.result;
        resolve(this.db);
      };

      request.onupgradeneeded = (event) => {
        const db = event.target.result;

        // Create stores if they don't exist
        if (!db.objectStoreNames.contains(STORES.TRIPS)) {
          db.createObjectStore(STORES.TRIPS, { keyPath: 'id' });
        }
        if (!db.objectStoreNames.contains(STORES.DESTINATIONS)) {
          const destStore = db.createObjectStore(STORES.DESTINATIONS, { keyPath: 'id' });
          destStore.createIndex('tripId', 'tripId', { unique: false });
        }
        if (!db.objectStoreNames.contains(STORES.PACKING_LISTS)) {
          db.createObjectStore(STORES.PACKING_LISTS, { keyPath: 'tripId' });
        }
        if (!db.objectStoreNames.contains(STORES.OFFLINE_DATA)) {
          db.createObjectStore(STORES.OFFLINE_DATA, { keyPath: 'key' });
        }
      };
    });

    return this.initPromise;
  }

  async getStore(storeName, mode = 'readonly') {
    await this.init();
    const transaction = this.db.transaction(storeName, mode);
    return transaction.objectStore(storeName);
  }

  // Trip offline data methods
  async saveTripOffline(tripData) {
    const store = await this.getStore(STORES.TRIPS, 'readwrite');
    return new Promise((resolve, reject) => {
      const request = store.put({
        ...tripData,
        savedAt: new Date().toISOString(),
        id: tripData.id || `trip_${Date.now()}`
      });
      request.onsuccess = () => resolve(request.result);
      request.onerror = () => reject(request.error);
    });
  }

  async getTripOffline(tripId) {
    const store = await this.getStore(STORES.TRIPS);
    return new Promise((resolve, reject) => {
      const request = store.get(tripId);
      request.onsuccess = () => resolve(request.result);
      request.onerror = () => reject(request.error);
    });
  }

  async getAllOfflineTrips() {
    const store = await this.getStore(STORES.TRIPS);
    return new Promise((resolve, reject) => {
      const request = store.getAll();
      request.onsuccess = () => resolve(request.result);
      request.onerror = () => reject(request.error);
    });
  }

  async deleteTripOffline(tripId) {
    const store = await this.getStore(STORES.TRIPS, 'readwrite');
    return new Promise((resolve, reject) => {
      const request = store.delete(tripId);
      request.onsuccess = () => resolve();
      request.onerror = () => reject(request.error);
    });
  }

  // Destination offline data methods
  async saveDestinationOffline(destinationData) {
    const store = await this.getStore(STORES.DESTINATIONS, 'readwrite');
    const id = `${destinationData.tripId}_${destinationData.name.toLowerCase().replace(/\s+/g, '_')}`;
    return new Promise((resolve, reject) => {
      const request = store.put({
        ...destinationData,
        id,
        savedAt: new Date().toISOString(),
        status: 'downloaded'
      });
      request.onsuccess = () => resolve(request.result);
      request.onerror = () => reject(request.error);
    });
  }

  async getDestinationOffline(destinationId) {
    const store = await this.getStore(STORES.DESTINATIONS);
    return new Promise((resolve, reject) => {
      const request = store.get(destinationId);
      request.onsuccess = () => resolve(request.result);
      request.onerror = () => reject(request.error);
    });
  }

  async getDestinationsByTrip(tripId) {
    const store = await this.getStore(STORES.DESTINATIONS);
    const index = store.index('tripId');
    return new Promise((resolve, reject) => {
      const request = index.getAll(tripId);
      request.onsuccess = () => resolve(request.result);
      request.onerror = () => reject(request.error);
    });
  }

  async deleteDestinationOffline(destinationId) {
    const store = await this.getStore(STORES.DESTINATIONS, 'readwrite');
    return new Promise((resolve, reject) => {
      const request = store.delete(destinationId);
      request.onsuccess = () => resolve();
      request.onerror = () => reject(request.error);
    });
  }

  // Packing list methods
  async savePackingList(tripId, packingData) {
    const store = await this.getStore(STORES.PACKING_LISTS, 'readwrite');
    return new Promise((resolve, reject) => {
      const request = store.put({
        tripId,
        ...packingData,
        savedAt: new Date().toISOString()
      });
      request.onsuccess = () => resolve(request.result);
      request.onerror = () => reject(request.error);
    });
  }

  async getPackingList(tripId) {
    const store = await this.getStore(STORES.PACKING_LISTS);
    return new Promise((resolve, reject) => {
      const request = store.get(tripId);
      request.onsuccess = () => resolve(request.result);
      request.onerror = () => reject(request.error);
    });
  }

  async deletePackingList(tripId) {
    const store = await this.getStore(STORES.PACKING_LISTS, 'readwrite');
    return new Promise((resolve, reject) => {
      const request = store.delete(tripId);
      request.onsuccess = () => resolve();
      request.onerror = () => reject(request.error);
    });
  }

  // Storage usage estimation
  async getStorageUsage() {
    if (navigator.storage && navigator.storage.estimate) {
      const estimate = await navigator.storage.estimate();
      return {
        used: estimate.usage || 0,
        quota: estimate.quota || 0,
        usedMB: ((estimate.usage || 0) / (1024 * 1024)).toFixed(2),
        quotaMB: ((estimate.quota || 0) / (1024 * 1024)).toFixed(2)
      };
    }
    return { used: 0, quota: 0, usedMB: '0', quotaMB: '0' };
  }

  // Clear all offline data
  async clearAllData() {
    const stores = [STORES.TRIPS, STORES.DESTINATIONS, STORES.PACKING_LISTS, STORES.OFFLINE_DATA];
    for (const storeName of stores) {
      const store = await this.getStore(storeName, 'readwrite');
      await new Promise((resolve, reject) => {
        const request = store.clear();
        request.onsuccess = () => resolve();
        request.onerror = () => reject(request.error);
      });
    }
  }
}

export const offlineStorage = new OfflineStorageService();
export default offlineStorage;
